import React, { useCallback, useEffect, useState, useMemo } from "react";
import _ from "lodash";
import { Select, Space, Typography, Switch, Tooltip } from "antd";
import { DeleteIcon, ExternalLinkIcon } from "components/Icons";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { TableContainer } from "components/TableContainer";
import { CellRenderers, getFilterProps, Sorters } from "utils/tableUtils";
import NameAvatar from "components/NameAvatar";
// import { AppSecToolsIntegrationAPI } from "restAPI/ToolsIntegrationAPI";
import ModalUtil from "utils/modalUtil";
import DeletePopup from "components/DeletePopup";
// import { getToolsWithCategory, ToolsData } from "views/Integrations/new/util";
import { ToolsData } from "views/Integrations/new/util";

const ToolSelector = ({ productID, value, onChange, existingSubproduct, ...props }) => {
  const [adminRecomendedTools, setAdminRecomendedTools] = useState();
  const [initialValue] = useState(value);
  const [toolsOptions, setToolsOptions] = useState();
  const [allTools] = useState(() => _.map(ToolsData, ({name: toolName, category, icon, displayName }) => ({toolName, type: _.isArray(category) ? category : [category], icon, displayName })));
  const [configuredTools, setConfiguredTools] = useState(); //existingSubproduct.subProductToolsMap

  useEffect(() => {
    const { promise, cancel } = GlobalSettingAPI.getCustomizedTool();
    // const { promise: promise2, cancel: cancel2 } = AppSecToolsIntegrationAPI.getToolsMetaData();

    // Promise.all([promise, promise2]).then(([adminTools, allTools]) => {
    promise.then((adminTools) => {
      setAdminRecomendedTools(() => filterToolData(adminTools));
      // setAllTools(allTools);
    });

    return cancel;

    // return () => {
    //   cancel && cancel();
    //   cancel2 && cancel2();
    // };
  }, []);

  useEffect(() => {
    if (_.isUndefined(adminRecomendedTools)) {
      return;
    }

    // const allTools = getToolsWithCategory(true);
    const toolOptions = [];
    const configuredTools = [];

    _.forEach(allTools, entry => {
      const adminRecomended = _.find(adminRecomendedTools, { toolName: entry.toolName })
      const isAdminRecomended = !_.isEmpty(adminRecomended);
      const isToolAdded = _.has(initialValue, entry.toolName);

      const data = {
        ...entry,
        ...(adminRecomended ? {
          ...adminRecomended,
          isAdminRecomended: true
        } : {
          isAdminRecomended: false
        }),
        isExcluded: (isToolAdded && isAdminRecomended) ? initialValue[entry.toolName] : false,
        alreadyAdded: isToolAdded || isAdminRecomended,
        isUserAdded: isToolAdded && !isAdminRecomended,
      };

      if (isAdminRecomended || isToolAdded) {
        configuredTools.push(data);
      }

      toolOptions.push(data);
    });

    setConfiguredTools(configuredTools);
    setToolsOptions(toolOptions);
  }, [adminRecomendedTools, allTools, initialValue]);

  const onUpdate = useCallback((tools) => {
    const toolMap = {};

    _.forEach(tools, entry => {
      if (entry.isAdminRecomended && entry.isExcluded) {
        toolMap[entry.toolName] = true;
      } else if (entry.isUserAdded) {
        toolMap[entry.toolName] = false;
      }
    });

    onChange && onChange(toolMap)
  }, [onChange]);

  const onToolStateChange = useCallback((checked, record) => {
    if (checked) {
      ModalUtil.show({
        content: <DeletePopup
          promptMessage={<>
            <p>Are you sure you want to excluded this admin recommended tool?</p>
          </>}
          note={true}
          okText={'Done'}
          onDelete={() => {
            setConfiguredTools(configuredTools => {
              const currentTool = _.find(configuredTools, { toolName: record.toolName });
              currentTool.isExcluded = true;

              onUpdate(configuredTools);
              return _.slice(configuredTools);
            });
          }}
        />
      })
    } else {
      setConfiguredTools(configuredTools => {
        const currentTool = _.find(configuredTools, { toolName: record.toolName });
        currentTool.isExcluded = false;
        onUpdate(configuredTools);
        return _.slice(configuredTools);
      });
    }

  }, [onUpdate]);

  const onRemove = useCallback((record) => {
    setConfiguredTools(configuredTools => {
      _.remove(configuredTools, { toolName: record.toolName });
      onUpdate(configuredTools);
      return _.slice(configuredTools);
    });

    setToolsOptions(toolsOptions => {
      const newOptions = [...toolsOptions];
      const currentTool = _.find(newOptions, { toolName: record.toolName });
      currentTool.alreadyAdded = false;
      currentTool.isUserAdded = false;
      currentTool.isExcluded = false;
      return newOptions;
    });
  }, [onUpdate]);

  const onCell = useCallback((record) => ({
    style: {
      opacity: record.isExcluded ? 0.5 : 1,
    }
  }), []);

  const columns = useMemo(() => [
    {
      title: "Name",
      dataIndex: "toolName",
      onCell,
      render: (toolName, record) => {
        return (
          <Space>
            <NameAvatar size="small" icon={record.icon} name={toolName}/> 
            <div>
              <div className='text-bold'>{toolName}</div>
              <div style={{ fontSize: 12 }}>{record.isAdminRecomended ? 'Admin Recommended' : 'User Defined'}</div>
            </div>
          </Space>
        )
      },
      sorter: Sorters.STRING("toolName"),
      ...getFilterProps({dataIndex: "toolName", dataSource: configuredTools, exactMatch: false  }),
    },
    {
      title: "Type",
      dataIndex: "type",
      onCell,
      render: CellRenderers.TAGS,
      ...getFilterProps({dataIndex: "type", dataSource: configuredTools, exactMatch: false  }),
    },
    {
      title: "Excluded",
      dataIndex: "isExcluded",
      render: (isExcluded, record) => {
        if (record.isUserAdded) {
          return "";
        }
        return <Switch checked={isExcluded} size="small" onChange={(checked) => onToolStateChange(checked, record)} />
      }
    },
    {
      title: "Action",
      dataIndex: "id",
      onCell,
      render: (id, record) => {
        if (record.isUserAdded) {
          return <><Typography.Link href={`#/security-tools/${record.toolName}`} target="_blank">
            <Tooltip title="Configure In New Tab"><ExternalLinkIcon className={"m-r-xs"} /></Tooltip>
          </Typography.Link><Tooltip title="Remove"><DeleteIcon className="danger" onClick={() => onRemove(record)} /></Tooltip></>;
        }
        return record.isExcluded ? <ExternalLinkIcon className={record.isExcluded ? "disabled" : ""} /> :
          <Typography.Link href={`#/security-tools/${record.toolName}`} target="_blank"><ExternalLinkIcon /></Typography.Link>;
      }
    }
  ], [onCell, configuredTools, onToolStateChange, onRemove]);

  const onToolSelect = useCallback((value, record) => {
    if (!record.alreadyAdded) {
      const newOptions = [...toolsOptions];
      const currentTool = _.find(newOptions, { toolName: record.toolName });
      currentTool.alreadyAdded = true;
      currentTool.isUserAdded = true;
      currentTool.isExcluded = false;

      onUpdate([...configuredTools, currentTool]);

      setConfiguredTools(configuredTools => {
        return [...configuredTools, currentTool];
      });
      setToolsOptions(newOptions);
    }
  }, [configuredTools, onUpdate, toolsOptions]);

  return (
    <>
      <Select value={null} filterOption={filterToolOptions} placeholder="Search and Add Tool" showSearch={true} style={{ width: "350px" }} options={parseSelectOptions(toolsOptions)} onChange={onToolSelect} />
      {
        <TableContainer
          loading={_.isUndefined(adminRecomendedTools)}
          dataSource={configuredTools}
          columns={columns}
          rowKey="name"
          pagination={false}
        />
      }
    </>
  )
}
export default ToolSelector;

const parseSelectOptions = (tools) => {
  return _.map(tools, entry => {
    const displayName = entry.displayName ? entry.displayName : entry.toolName
    return ({
      label: <div className="d-flex">
        <Space>
          <NameAvatar size="small" icon={entry.icon} name={displayName}/> 
          <div>
            <div className='text-bold'>{displayName}</div>
            <div style={{ fontSize: 12 }}>{_.join(entry.type, " ")}</div>
          </div>
        </Space>
        <div style={{ flex: 'auto', color: '#069637', textAlign: "right" }}>{entry.alreadyAdded ? "Added" : ""}</div>
      </div>, value: entry.toolName, ...entry
    })
  });
};

const filterToolData = (data) => {
  if (_.isEmpty(data)) {
    return data
  }
  return data.filter(item => item.toolName !== "CUSTOM")
}

const filterToolOptions = (input, value) => {
  input = _.lowerCase(input);
  const applyFilter = (itemName) => {
    if (typeof itemName !== 'string') {
      return false;
    }
    return itemName.toLowerCase().trim().includes(input);
  }

  let isResultFound = applyFilter(value.displayName ? value.displayName : value.toolName);
  if (isResultFound) {
    return true;
  }
  isResultFound = applyFilter(value.type);
  if (value.type instanceof Array) {
    isResultFound = value.type.some(applyFilter);
  }
  return isResultFound;
}
